import { memo, useCallback, useEffect, useState } from 'react';
import { Button, Modal } from 'react-daisyui';
import { X } from 'lucide-react';
import { connect } from 'react-redux';
import { BillingActions } from 'store/actions';
import { getPractice } from 'library/auth';
import toast from 'react-hot-toast';
import { PortalSpinner } from 'components/PortalSpinner';
import {
  KEYPAD_ROWS,
  applyConvenienceFee,
  getChargePayload,
  removeConvenienceFee,
  handleBackSpace,
  addNewDigitIntoValue,
  getDigitsFontSize,
  dollarSignClass
} from './helper';
import { formatAmount } from 'utils/helpers';
import { useNavigate } from 'react-router-dom';
import { PORTAL_ROUTES } from 'utils/constants';
import { debounce } from "lodash";

let enhancer = connect(null, { ...BillingActions });

const PaymentModal = ({ open, onClose, createCharge }) => {
  const navigate = useNavigate();
  const [digits, setDigits] = useState('0.00');
  const [withoutFee, setWithoutFee] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isApplyingFee, setIsApplyingFee] = useState(false);

  const feePercentage = getPractice()?.patient_convenience_fee_percentage ?? 0;
  const showFeeCheckbox = digits.length > 0 && feePercentage > 0;

  useEffect(() => {
    if (withoutFee) {
      setDigits(removeConvenienceFee(Number(digits), feePercentage).toFixed(2))
    } else {
      setDigits(applyConvenienceFee(Number(digits), feePercentage).toFixed(2))
    }
  }, [withoutFee])

  const handleClose = () => {
    setDigits('0.00');
    setWithoutFee(false);
    onClose();
  };

  const handleKey = (key) => {
    if (key === 'clear') {
      setDigits('0.00');
      handler(Number(0.00), feePercentage)
      return;
    }
    if (key === 'backspace') {
      setDigits(prev => {
        const withDecimalNum = handleBackSpace(prev);
        if (!withoutFee) {
          setIsApplyingFee(true)
          handler(Number(withDecimalNum), feePercentage)
        }
        return withDecimalNum
      });
      return;
    }

    if (digits.length > 20) {
      toast.error("Amount is too large");
      return;
    }

    setDigits(prev => {
      let withDecimalNum = addNewDigitIntoValue(prev, key);
      if (!withoutFee) {
        setIsApplyingFee(true)
        handler(Number(withDecimalNum), feePercentage)
      }
      return withDecimalNum
    });
  };

  const assignFeePercentage = (amount, percentage) => {
    setDigits(applyConvenienceFee(amount, percentage).toFixed(2))
    setIsApplyingFee(false)
  }

  const handler = useCallback(debounce(assignFeePercentage, 1500), [feePercentage]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let { data } = await createCharge({ without_fee: withoutFee }, getChargePayload(withoutFee, digits, feePercentage));
      handleClose();
      navigate(`${PORTAL_ROUTES.CHARGES}/${data?.attributes?.encoded_id}`);
    } catch (error: any) {
      toast.error(error.errors || 'Failed to process payment. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <Modal open={open} onClose={handleClose} backdrop className="max-w-sm rounded-2xl border border-gray-100 shadow-2xl">
      <Modal.Body className="p-6">
        {submitting ? (
          <PortalSpinner />
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-bold text-secondary-900">Create Payment</h2>
              <Button size="sm" color="ghost" type="button" onClick={handleClose} className="rounded-lg text-secondary-400">
                <X size={18} />
              </Button>
            </div>

            <div className="flex flex-col items-center py-8 gap-3">
              <div className="flex items-end gap-1">
                <span className={`${dollarSignClass(digits.length)} font-semibold text-secondary-400`}>$</span>
                <span className={`${getDigitsFontSize(digits.length)} font-bold text-secondary-900 leading-none tracking-tight`}>
                  {digits}
                </span>
              </div>

              {isApplyingFee ? (
                <div className="flex flex-col items-center gap-1">
                  <div className="skeleton h-2 w-44"></div>
                  <div className="skeleton h-2 w-44"></div>
                </div>
              ) : (
                <>
                  {showFeeCheckbox && (
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-primary checkbox-xs [--chkfg:white]"
                        checked={!withoutFee}
                        onChange={() => setWithoutFee(prev => !prev)}
                      />
                      <span className="text-sm font-semibold text-blue-500">Convenience fee added.</span>
                    </label>
                  )}
                </>
              )}
            </div>

            <div className="grid grid-cols-3 mb-5">
              {KEYPAD_ROWS.flat().map((key) => (
                <Button
                  key={key}
                  type="button"
                  color="ghost"
                  onClick={() => handleKey(key)}
                  className="h-14 rounded-none font-semibold text-xl active:scale-95 border-0 bg-transparent text-secondary-900 hover:bg-gray-100"
                >
                  {key === 'backspace' ? '‹' : key === 'clear' ? '×' : key}
                </Button>
              ))}
            </div>

            <Button
              fullWidth
              disabled={digits == '0.00' || isApplyingFee}
              onClick={handleSubmit}
              className="bg-secondary-900 text-white rounded-xl h-12 text-base font-semibold hover:bg-secondary-800 disabled:opacity-40 disabled:cursor-not-allowed border-none"
            >
              Submit ${formatAmount(digits)}
            </Button>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default enhancer(memo(PaymentModal));
